import { toJS } from 'mobx'
import * as api from '../lib/api'
import moduleLabel from './modulelabel'

/**
 * Construit une vue d'ensemble en lecture seule de tous les switches d'une expérience.
 *
 * Parcourt l'ensemble de l'expérience (démarrage, compte à rebours, parcours, points,
 * scénarios, modules) pour recenser :
 *  - les endroits qui *écrivent* un switch (actions add_switch / delete_switch),
 *  - les endroits qui *testent* un switch (conditions des modules Switch).
 * Puis recoupe les deux pour signaler les incohérences exactes.
 *
 * Ne modifie rien. Ne prend pas en compte les méthodes isSolution / isNotSolution
 * (qui référencent une enquête, pas un switch réel).
 *
 * @param {string} experienceId
 * @param {boolean} noCache
 * @returns {Promise<SwitchOverview>}
 */
export const buildSwitchOverview = async (experienceId, noCache = false) => {
  const [expRaw, tracksRaw, pointsRaw, scenariosRaw, modulesRaw] = await Promise.all([
    api.experiences.get(experienceId, noCache),
    api.tracks.getFrom('experiences', experienceId, false, noCache),
    api.points.getFrom('experiences', experienceId, false, noCache),
    api.scenarios.getFrom('experiences', experienceId, false, noCache),
    api.modules.getFrom('experiences', experienceId, false, noCache)
  ])

  const exp = toJS(expRaw) || {}
  const tracks = (toJS(tracksRaw) || []).filter(Boolean)
  const points = (toJS(pointsRaw) || []).filter(Boolean)
  const scenarios = (toJS(scenariosRaw) || []).filter(Boolean)
  const modules = (toJS(modulesRaw) || []).filter(Boolean)

  const siteId = exp.siteId
  const expId = exp._id
  if (!siteId || !expId) return emptyOverview()

  const base = `/site/${siteId}/experience/${expId}`
  const trackById = new Map(tracks.map(t => [t._id, t]))

  // key -> [{ source: { path, label }, value, actionType }]
  const writes = {}
  // key -> [{ source: { path, label }, method, value }]
  const reads = {}

  const addWrite = (key, source, value, actionType) => {
    if (!key) return
    if (!writes[key]) writes[key] = []
    writes[key].push({ source, value, actionType })
  }

  const addRead = (key, source, method, value) => {
    if (!key) return
    if (!reads[key]) reads[key] = []
    reads[key].push({ source, method: method || 'equal', value })
  }

  const scanAction = (action, source) => {
    if (!action || typeof action !== 'object') return
    if (action.type === 'add_switch' && action.key) addWrite(action.key, source, action.value ?? '', 'add_switch')
    else if (action.type === 'delete_switch' && action.key) addWrite(action.key, source, undefined, 'delete_switch')
  }

  const scanActions = (actions, source) => (actions || []).forEach(action => scanAction(action, source))

  // Parcours récursif d'un objet à la recherche de listes d'actions (spécificités des modules).
  const findAndScanActions = (obj, keys, source) => {
    for (const key in obj) {
      if (!Object.prototype.hasOwnProperty.call(obj, key)) continue
      const value = obj[key]
      if (value && typeof value === 'object') {
        if (keys.includes(key) && Array.isArray(value)) scanActions(value, source)
        else findAndScanActions(value, keys, source)
      }
    }
  }

  // --- Écritures ---

  // Au démarrage de l'expérience.
  scanActions(exp.onStart, { path: `${base}#root_onStart`, label: "Au démarrage de l'expérience" })

  // Comptes à rebours.
  Object.values(exp.timers || {}).forEach(timer => {
    if (timer?._id) {
      scanActions(timer.actions, {
        path: `${base}/timer/${timer._id}`,
        label: `Compte à rebours « ${timer.name || timer._id} »`
      })
    }
  })

  // Parcours (déclencheurs et menus).
  tracks.forEach(track => {
    const trackName = track.name || track._id
    ;(track.events || []).forEach(event =>
      scanActions(event.actions, { path: `${base}/track/${track._id}#root_events`, label: `Déclencheurs du parcours « ${trackName} »` })
    )
    ;(track.menu || []).forEach(menu =>
      menu?.action && scanAction(menu.action, { path: `${base}/track/${track._id}#root_menu`, label: `Menu du parcours « ${trackName} »` })
    )
  })

  // Points (premières actions et actions de déclencheur).
  points.forEach(point => {
    if (!point.trackId || !point._id) return
    const trackName = trackById.get(point.trackId)?.name || point.trackId
    const pointName = point.internalName || point.name || point._id
    const source = {
      path: `${base}/track/${point.trackId}/point/${point._id}/trigger`,
      label: `Point « ${pointName} » (parcours « ${trackName} »)`
    }
    scanActions(point.firstActions, source)
    scanActions(point.actions, source)
  })

  // Scénarios (actions de fin).
  scenarios.forEach(scenario => {
    scanActions(scenario.onEnd, {
      path: `${base}/scenario/${scenario._id}#root_onEnd`,
      label: `Fin du scénario « ${scenario.name || scenario._id} »`
    })
  })

  // Modules.
  modules.forEach(module => {
    const modulePath = `${base}/scenario/${module.scenarioUid}/module/${module._id}`
    const name = moduleName(module)
    const typeLabel = moduleLabel[module.type] || module.type || 'Module'

    // onNext.
    ;(module.onNext || []).forEach(obj =>
      scanActions(obj.actions, { path: `${modulePath}#root_onNext`, label: `Module « ${name} » (${typeLabel}) — actions « suivant »` })
    )
    // Bouton outil.
    if (module.toolButton?.action)
      scanAction(module.toolButton.action, { path: `${modulePath}#root_toolButton`, label: `Module « ${name} » (${typeLabel}) — bouton outil` })
    // Boutons outils.
    ;(module.listToolButtons || []).forEach(b =>
      b?.action && scanAction(b.action, { path: `${modulePath}#root_listToolButtons`, label: `Module « ${name} » (${typeLabel}) — boutons outils` })
    )

    // Actions spécifiques du module (cas de Switch, succès/échec, indices, etc.).
    const rest = { ...module }
    delete rest.onNext
    delete rest.toolButton
    delete rest.listToolButtons
    findAndScanActions(rest, ['actions', 'onFocus', 'onTouch', 'defaultActions'], {
      path: modulePath,
      label: `Module « ${name} » (${typeLabel})`
    })
  })

  // --- Lectures (conditions des modules Switch) ---
  modules
    .filter(module => module.type === 'switch' && module.scenarioUid)
    .forEach(module => {
      const source = {
        path: `${base}/scenario/${module.scenarioUid}/module/${module._id}`,
        label: `Module Switch « ${moduleName(module)} »`
      }
      ;(module.cases || []).forEach(c => {
        const conditions = []
        if (c.key || c.value || c.method) conditions.push({ key: c.key, value: c.value, method: c.method })
        if (Array.isArray(c.conditions)) conditions.push(...c.conditions)
        conditions.forEach(condition => {
          if (!condition?.key) return
          if (INVESTIGATION_METHODS.includes(condition.method)) return
          addRead(condition.key, source, condition.method, condition.value)
        })
      })
    })

  // --- Recoupement ---
  const keys = [...new Set([...Object.keys(writes), ...Object.keys(reads)])].sort((a, b) => a.localeCompare(b))

  const switches = keys.map(key => {
    const keyWrites = writes[key] || []
    const keyReads = reads[key] || []
    const writtenValues = [...new Set(keyWrites.filter(w => w.actionType === 'add_switch' && w.value !== undefined).map(w => w.value))]
    const testedValues = [...new Set(keyReads.filter(r => r.value != null && r.value !== '').map(r => r.value))]

    const warnings = []
    if (keyWrites.length && !keyReads.length) warnings.push('written-never-read')
    if (keyReads.length && !keyWrites.length) warnings.push('read-never-written')
    // Une valeur testée en égalité stricte qui n'est jamais posée ne pourra jamais matcher.
    // On ignore les switches numériques incrémentaux (+N / -N) dont la valeur est calculée au runtime.
    const writtenSet = new Set(writtenValues)
    const hasIncrementWrite = keyWrites.some(w => w.actionType === 'add_switch' && /^[-+]\d+$/.test(String(w.value)))
    const hasUnreachableValue = keyReads.some(r => r.method === 'equal' && r.value != null && r.value !== '' && !writtenSet.has(r.value))
    if (keyWrites.length && hasUnreachableValue && !hasIncrementWrite) warnings.push('value-never-written')

    return { key, writes: keyWrites, reads: keyReads, writtenValues, testedValues, warnings }
  })

  return {
    switches,
    stats: {
      total: switches.length,
      orphanWrites: switches.filter(s => s.warnings.includes('written-never-read')).length,
      orphanReads: switches.filter(s => s.warnings.includes('read-never-written')).length
    }
  }
}

const INVESTIGATION_METHODS = ['isSolution', 'isNotSolution']

const moduleName = module => module.internalName || module.title || module._id

const emptyOverview = () => ({ switches: [], stats: { total: 0, orphanWrites: 0, orphanReads: 0 } })
